import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import Button from '@mui/material/Button';
import CloseIcon from '@mui/icons-material/Close';

import MemoryActions from '../MemoryActions/MemoryActions';
import ShowVideo from 'components/UI/ShowVideo/ShowVideo';
import DeleteMemory from '../DeleteMemory/DeleteMemory';

import classes from './Memory.module.css';

const Memory = props => {
  const [hideMemoryId, setHideMemoryId] = useState(0);
  const [localFullScreen, setLocalFullScreen] = useState(false);

  const isFullScreen =
    props.isFullScreen !== undefined
      ? props.isFullScreen
      : localFullScreen;
  const setIsFullScreen =
    props.onToggleFullScreen || setLocalFullScreen;

  const hideMemoryHandler = id => {
    setHideMemoryId(id);
  };

  const prevMemory = () => {
    if (props.prevMemoryHandler) {
      props.prevMemoryHandler();
    }
  };

  const nextMemory = () => {
    if (props.nextMemoryHandler) {
      props.nextMemoryHandler();
    }
  };

  useEffect(() => {
    if (!isFullScreen) return;

    const handleKeyDown = e => {
      if (e.key === 'Escape') {
        setIsFullScreen(false);
      } else if (e.key === 'ArrowLeft') {
        if (props.prevMemoryHandler) {
          props.prevMemoryHandler();
        }
      } else if (e.key === 'ArrowRight') {
        if (props.nextMemoryHandler) {
          props.nextMemoryHandler();
        }
      }
    };

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = originalOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [
    isFullScreen,
    props.prevMemoryHandler,
    props.nextMemoryHandler,
    setIsFullScreen
  ]);

  const isVideo =
    props.dataMemory.mediaType !== 'png' &&
    props.dataMemory.mediaType !== 'jpeg' &&
    props.dataMemory.mediaType !== 'jpg' &&
    props.dataMemory.mediaType !== 'image' &&
    !(
      props.dataMemory.mediaType === 'mp4' &&
      props.dataMemory.thumbnail === null
    );

  return (
    <>
      <section
        className={
          hideMemoryId === props.dataMemory.memoryId
            ? classes.memoryNoShow
            : classes.memorySection
        }
      >
        {(props.dataMemory.isOwnerMemory ||
          props.dataMemory.isOwnerTribut) && (
          <DeleteMemory
            idMemory={props.dataMemory.memoryId}
            tributId={props.tributId}
            loadData={props.loadData}
            hideMemory={hideMemoryHandler}
            onDelete={props.onDeleteMemory}
          />
        )}
        <div
          className={`${classes.memoryMediaWrapper} ${
            isVideo ? classes.videoMediaWrapper : ''
          }`}
        >
          {props.dataMemory.mediaType === 'png' ||
          props.dataMemory.mediaType === 'jpeg' ||
          props.dataMemory.mediaType === 'jpg' ? (
            <figure
              key={props.dataMemory.memoryId}
              className={classes.avatarMemory}
            >
              <img
                alt={`Memoria ${props.dataMemory.memoryId}`}
                className={classes.imgMemory}
                src={props.dataMemory.url}
              />
            </figure>
          ) : props.dataMemory.mediaType === 'mp4' &&
            props.dataMemory.thumbnail === null ? (
            <figure
              key={props.dataMemory.memoryId}
              className={classes.avatarMemory}
            >
              <img
                alt={`Memoria ${props.dataMemory.memoryId}`}
                className={classes.imgMemory}
                src="https://d3cohyhv4rpctw.cloudfront.net/videoProceso.jpg"
              />
            </figure>
          ) : (
            <ShowVideo
              key={props.dataMemory.memoryId}
              srcVideo={props.dataMemory.url}
              memoryView={props.memoryView}
              memoryId={props.dataMemory.memoryId}
              memoryPlaying={props.memoryPlaying}
              pauseVideo={props.pauseVideo}
              thumbnail={props.dataMemory.thumbnail}
              dataMemory={props.dataMemory}
              memory
            />
          )}

          <div className={classes.memoryOverlayGradient} />
          <div className={classes.memoryMetaContainer}>
            <p className={classes.memoryOwnerName}>
              {props.dataMemory.firstName}{' '}
              {props.dataMemory.lastName}
            </p>
            {props.dataMemory.published && (
              <p className={classes.memoryOwnerDate}>
                {props.dataMemory.published}
              </p>
            )}
          </div>
        </div>
      </section>
      <div className={classes.btnsCarouselContainer}>
        <Button
          aria-label="Memoria anterior"
          className={classes.navButton}
          onClick={prevMemory}
        >
          <img
            src="https://d1g6c01s9ssd7h.cloudfront.net/img/icon_Back_1.png"
            alt="Memoria anterior"
            className={classes.navIconImg}
          />
        </Button>
        <Button
          aria-label="Pantalla completa"
          className={classes.navButton}
          onClick={() => setIsFullScreen(true)}
        >
          <img
            src="https://d1g6c01s9ssd7h.cloudfront.net/img/icon_fullScreen.png"
            alt="Pantalla completa"
            className={classes.navIconImg}
          />
        </Button>
        <Button
          aria-label="Siguiente memoria"
          className={classes.navButton}
          onClick={nextMemory}
        >
          <img
            src="https://d1g6c01s9ssd7h.cloudfront.net/img/icon_Next_1.png"
            alt="Siguiente memoria"
            className={classes.navIconImg}
          />
        </Button>
      </div>
      {props.carousel}
      <MemoryActions
        tributId={props.tributId}
        memory
        dataMemory={props.dataMemory}
        hideMemoryId={hideMemoryId}
        loadData={props.loadData}
        memoryView={props.memoryView}
      />

      {isFullScreen &&
        createPortal(
          <div
            className={classes.fullScreenOverlay}
            role="dialog"
            aria-modal="true"
            aria-label="Vista en pantalla completa"
          >
            <button
              type="button"
              className={classes.fullScreenCloseBtn}
              onClick={() => setIsFullScreen(false)}
              aria-label="Cerrar pantalla completa"
            >
              <CloseIcon className={classes.fullScreenCloseIcon} />
            </button>

            <button
              type="button"
              className={`${classes.fullScreenNavBtn} ${classes.fullScreenNavLeft}`}
              onClick={prevMemory}
              aria-label="Memoria anterior"
            >
              <img
                src="https://d1g6c01s9ssd7h.cloudfront.net/img/icon_Back_1.png"
                alt="Anterior"
                className={classes.fullScreenNavImg}
              />
            </button>

            <div className={classes.fullScreenMediaContainer}>
              {!isVideo ? (
                <img
                  src={
                    props.dataMemory.mediaType === 'mp4' &&
                    props.dataMemory.thumbnail === null
                      ? 'https://d3cohyhv4rpctw.cloudfront.net/videoProceso.jpg'
                      : props.dataMemory.url
                  }
                  alt={
                    props.dataMemory.message ||
                    `Memoria ${props.dataMemory.memoryId}`
                  }
                  className={classes.fullScreenImage}
                />
              ) : (
                <div className={classes.fullScreenVideoWrapper}>
                  <ShowVideo
                    key={`fs-${props.dataMemory.memoryId}`}
                    srcVideo={props.dataMemory.url}
                    memoryView={props.memoryView}
                    memoryId={props.dataMemory.memoryId}
                    memoryPlaying={props.memoryPlaying}
                    pauseVideo={props.pauseVideo}
                    thumbnail={props.dataMemory.thumbnail}
                    dataMemory={props.dataMemory}
                    memory
                    isFullScreen
                  />
                </div>
              )}
            </div>

            <button
              type="button"
              className={`${classes.fullScreenNavBtn} ${classes.fullScreenNavRight}`}
              onClick={nextMemory}
              aria-label="Siguiente memoria"
            >
              <img
                src="https://d1g6c01s9ssd7h.cloudfront.net/img/icon_Next_1.png"
                alt="Siguiente"
                className={classes.fullScreenNavImg}
              />
            </button>
          </div>,
          document.body
        )}
    </>
  );
};

Memory.propTypes = {
  // eslint-disable-next-line react/require-default-props
  carousel: PropTypes.node,
  // eslint-disable-next-line react/require-default-props
  tributId: PropTypes.number,
  // eslint-disable-next-line react/require-default-props
  dataMemory: PropTypes.object,
  // eslint-disable-next-line react/require-default-props
  loadData: PropTypes.func,
  // eslint-disable-next-line react/require-default-props
  memoryView: PropTypes.number,
  // eslint-disable-next-line react/require-default-props
  memoryPlayingHandler: PropTypes.func,
  // eslint-disable-next-line react/require-default-props
  memoryPlaying: PropTypes.number,
  // eslint-disable-next-line react/require-default-props
  pauseVideo: PropTypes.bool,
  // eslint-disable-next-line react/require-default-props
  prevMemoryHandler: PropTypes.func,
  // eslint-disable-next-line react/require-default-props
  nextMemoryHandler: PropTypes.func,
  // eslint-disable-next-line react/require-default-props
  isFullScreen: PropTypes.bool,
  // eslint-disable-next-line react/require-default-props
  onToggleFullScreen: PropTypes.func,
  // eslint-disable-next-line react/require-default-props
  onDeleteMemory: PropTypes.func
};

export default Memory;
